import { ArrowLeft, Clock, Layers, Loader2, Plus, Presentation, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { presentationsService } from "@/lib/services";
import type { PresentationProjectStatus, PresentationProjectSummary } from "@/types";

const STATUS_LABELS: Record<PresentationProjectStatus, string> = {
  draft: "Draft",
  outlining: "Outlining...",
  outline_ready: "Outline Ready",
  generating: "Generating...",
  generated: "Generated",
  refining: "Refining...",
  complete: "Complete",
};

const STATUS_COLORS: Record<PresentationProjectStatus, string> = {
  draft: "bg-slate-500/20 text-slate-400",
  outlining: "bg-amber-500/20 text-amber-400",
  outline_ready: "bg-blue-500/20 text-blue-400",
  generating: "bg-amber-500/20 text-amber-400",
  generated: "bg-emerald-500/20 text-emerald-400",
  refining: "bg-amber-500/20 text-amber-400",
  complete: "bg-emerald-500/20 text-emerald-400",
};

export function PresentationsProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PresentationProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await presentationsService.listProjects(1, 50);
      setProjects(response.data);
    } catch (err) {
      console.error("Failed to load projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/apps")}
              className="p-1.5 rounded-lg hover:bg-surface-secondary text-content-tertiary hover:text-content-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <Presentation className="w-5 h-5 text-mutual-orange" />
              <h1 className="text-lg font-semibold text-content-primary">Presentations</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50 w-56"
              />
            </div>
            <button
              onClick={() => navigate("/apps/presentations/new")}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-gradient text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              New Presentation
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-content-tertiary" />
              </div>
            );
          }
          if (filteredProjects.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Presentation className="w-12 h-12 text-content-tertiary mb-3" />
                <h3 className="text-lg font-medium text-content-primary mb-1">
                  {searchQuery ? "No matching projects" : "No presentations yet"}
                </h3>
                <p className="text-sm text-content-tertiary mb-4">
                  {searchQuery ? "Try a different search term" : "Create your first AI-powered presentation"}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate("/apps/presentations/new")}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gradient text-white text-sm font-medium hover:opacity-90 transition-opacity"
                  >
                    <Plus className="w-4 h-4" />
                    New Presentation
                  </button>
                )}
              </div>
            );
          }
          return (
            <div className="grid gap-3">
              {filteredProjects.map((project) => (
                <div
                  key={project.uuid}
                  role="button" // NOSONAR — contains nested interactive elements
                  tabIndex={0}
                  onClick={() => navigate(`/apps/presentations/${project.uuid}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/apps/presentations/${project.uuid}`);
                    }
                  }}
                  className="flex items-center gap-4 p-4 rounded-xl border border-border-primary bg-surface-primary hover:bg-surface-secondary cursor-pointer transition-colors group"
                >
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-mutual-orange/10 flex items-center justify-center">
                    <Presentation className="w-5 h-5 text-mutual-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-content-primary truncate">{project.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>
                        {STATUS_LABELS[project.status]}
                      </span>
                    </div>
                    {project.description && (
                      <p className="text-xs text-content-tertiary truncate">{project.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-content-tertiary">
                    <div className="flex items-center gap-1">
                      <Layers className="w-3.5 h-3.5" />
                      <span>{project.slideCount} slides</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{formatDate(project.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
